import { FaroText, FilterMenuOptionType, green, red } from "@faro-lotv/flat-ui";
import { Box, Stack } from "@mui/material";
import { useMemo, type JSX } from "react";
import { ChipFilterWithSearch } from "./chip-filter-with-search";

/** AI annotation status options */
export enum AiAnnotationStatus {
  /** Good confidence (approved) AI annotations */
  GoodConfidence = "good-confidence",
  /** Bad confidence (rejected) AI annotations */
  BadConfidence = "bad-confidence",
}

export type AiStatusFilterProps = {
  /** List of selected AI status keys */
  selectedAiStatus: AiAnnotationStatus[];

  /** Callback executed when the selection changes */
  onSelectedAiStatusChanged(statuses: AiAnnotationStatus[]): void;
};

type AiStatusIconProps = {
  /** The AI status to represent */
  status: AiAnnotationStatus;
};

type Colors = {
  /** The color of the outer border */
  border: string;
  /** The color of the background of the icon */
  background: string;
  /** The color of the central element */
  element: string;
};

/** The color scheme based on the AI status */
const AI_STATUS_ICON_COLORS: Record<AiAnnotationStatus, Colors> = {
  [AiAnnotationStatus.GoodConfidence]: {
    border: green[200],
    background: green[50],
    element: green[600],
  },
  [AiAnnotationStatus.BadConfidence]: {
    border: red[200],
    background: red[50],
    element: red[600],
  },
};

/** The display names for AI statuses */
const AI_STATUS_LABELS: Record<AiAnnotationStatus, string> = {
  [AiAnnotationStatus.GoodConfidence]: "Good confidence (approved)",
  [AiAnnotationStatus.BadConfidence]: "Bad confidence (rejected)",
};

/** @returns A colored icon to represent a specific AI status */
function AiStatusIcon({ status }: AiStatusIconProps): JSX.Element {
  const colors = AI_STATUS_ICON_COLORS[status];

  return (
    <Box
      component="div"
      sx={{
        width: "24px",
        height: "24px",
        borderRadius: "12px",
        border: `1px solid ${colors.border}`,
        background: colors.background,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        component="div"
        sx={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          backgroundColor: colors.element,
        }}
      />
    </Box>
  );
}

/**
 * @returns the Option component for a specific AI status
 * @param status AI status value
 * @param displayName displayed name of the status
 */
function getAiStatusOptionComponent(
  status: AiAnnotationStatus,
  displayName: string,
): FilterMenuOptionType {
  return {
    key: status,
    value: displayName,
    label: (
      <Stack direction="row" sx={{ gap: 0.5, alignItems: "center" }}>
        <AiStatusIcon status={status} />
        <FaroText variant="bodyS">{displayName}</FaroText>
      </Stack>
    ),
    tooltip: (
      <Stack direction="row" sx={{ gap: 0.5, alignItems: "center" }}>
        <AiStatusIcon status={status} />
        <FaroText variant="bodyS" dark>
          {displayName}
        </FaroText>
      </Stack>
    ),
  };
}

/** @returns A chip with a popup that can be used to select AI annotation statuses */
export function AiStatusFilter({
  selectedAiStatus,
  onSelectedAiStatusChanged,
}: AiStatusFilterProps): JSX.Element {
  const options = useMemo<FilterMenuOptionType[]>(
    () =>
      Object.values(AiAnnotationStatus).map((status) =>
        getAiStatusOptionComponent(status, AI_STATUS_LABELS[status]),
      ),
    [],
  );

  const selectedOptions = useMemo<FilterMenuOptionType[]>(
    () =>
      selectedAiStatus
        .map((status) => options.find((option) => option.key === status))
        .filter((option) => !!option),
    [selectedAiStatus, options],
  );

  return (
    <ChipFilterWithSearch
      label="AI generated"
      selectedOptions={selectedOptions}
      onFilterChange={(selected) => {
        const newSelection = selected
          .map((option) => option.key)
          .filter(
            (key): key is AiAnnotationStatus =>
              key === AiAnnotationStatus.GoodConfidence ||
              key === AiAnnotationStatus.BadConfidence,
          );
        onSelectedAiStatusChanged(newSelection);
      }}
      options={options}
      headingLabel="Select AI statuses"
    />
  );
}
